import { staticFormApiConnector } from 'apiConnector/form/static';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { unhandledError } from 'state/actions';
import { commonFormActions } from 'state/forms/actions/form';
import { RoutePath } from 'state/router';
import type { EpicWithThunk } from 'state/state.common';
import { defaultFormReceivedThunk } from 'state/static/staticForm.thunks';
import { workspaceTabQuoteRepriced } from 'state/workspaces/workspace.actions';
import { workspaceRfqLoadRequestedThunk } from 'state/workspaces/workspace.thunks';
import { getWorkspaceTabIds } from 'state/workspaces/workspaces/workspaces.selector';

function waitForStaticWorkspaceReady(
  action$: Observable<any>,
  state: any,
  getCurrentRoute: (state: any) => string,
  navigateTo: (path: RoutePath, subRoutePath?: string) => void,
): Observable<boolean> {
  const currentRoute = getCurrentRoute(state);
  const isWorkspaceInitialized = !!getWorkspaceTabIds(state, 'static')?.length;

  const staticTabsInitialized$ = isWorkspaceInitialized
    ? of(true)
    : action$.pipe(
        ofType('WORKSPACE_PERSISTED_TABS_APPLIED', 'WORKSPACE_TAB_CREATED'),
        filter(({ workspace }) => workspace === 'static'),
        map(() => true),
        take(1),
      );

  const navigateToStaticScreenSucceed$ =
    currentRoute === '/static'
      ? of(true)
      : action$.pipe(
          ofType('ROUTE_CHANGED'),
          filter(({ path }) => path === '/static'),
          take(1),
        );

  if (currentRoute !== '/static') {
    navigateTo('/static');
  }

  return combineLatest([staticTabsInitialized$, navigateToStaticScreenSucceed$]).pipe(map(() => true));
}

export function staticWorkspaceRepriceEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getCurrentRoute }, getNewGuid, navigateTo }) => {
    return action$.pipe(
      ofType('WORKSPACE_TAB_REPRICE_REQUESTED'),
      filter(({ workspace }) => workspace === 'static'),
      withLatestFrom(state$),
      mergeMap(
        ([
          {
            id,
            idType,
            rfqId,
            pricerRfqId,
            externalQuoteId,
            withLastQuote,
            processingChain,
            shouldRedirectToWorkspace,
            aiGeneratedFields,
          },
          state,
        ]) => {
          const repriceAction = workspaceTabQuoteRepriced(
            getNewGuid(),
            id,
            idType,
            rfqId,
            pricerRfqId,
            'static',
            withLastQuote,
            processingChain,
            externalQuoteId,
            aiGeneratedFields,
          );

          if (!shouldRedirectToWorkspace) {
            return of(repriceAction);
          }

          return waitForStaticWorkspaceReady(action$, state, getCurrentRoute, navigateTo).pipe(
            map(() => repriceAction),
            catchError(err =>
              of(unhandledError('staticWorkspaceRepriceEpic failed', err, 'staticWorkspaceRepriceEpic')),
            ),
          );
        },
      ),
    );
  };
}

export function staticWorkspaceRfqReloadEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, state$, { httpClient }) => {
    return action$.pipe(
      ofType('WORKSPACE_RFQ_LOAD_REQUESTED'),
      mergeMap(({ tabId, rfqId }) => {
        const api = staticFormApiConnector(httpClient);
        const request = { tabId, rfqId };
        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        return api
          .initFrom(request)
          .pipe(
            map(staticForm => {
              const {
                productView,
                values,
                blocksMetadata,
                fieldsMetadata,
                version,
                actions,
                quoteQuery,
                errors,
                negotiationId,
                mustForceAdvancedMode,
                allowedRequests,
              } = staticForm;

              return defaultFormReceivedThunk(
                tabId,
                productView,
                blocksMetadata,
                fieldsMetadata,
                values,
                version,
                negotiationId,
                actions,
                quoteQuery,
                mustForceAdvancedMode,
                allowedRequests,
                errors,
              );
            }),
            catchError(error =>
              of(
                commonFormActions.defaultFormFailed('static', tabId, error.message || 'repriced failed', error.traceId),
              ),
            ),
            timeoutWith(timeoutInMillis, of(commonFormActions.defaultFormTimeouted('static', tabId)), appScheduler),
          )
          .pipe(takeUntil(tabRemoved$));
      }),
    );
  };
}

export function staticWorkspaceSharableQuoteLoadEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getCurrentRoute }, navigateTo }) => {
    return action$.pipe(
      ofType('WORKSPACE_SHARABLE_QUOTE_LOAD_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ rfqId }, state]) => {
        return waitForStaticWorkspaceReady(action$, state, getCurrentRoute, navigateTo).pipe(
          map(() => workspaceRfqLoadRequestedThunk(rfqId)),
        );
      }),
    );
  };
}
